import { useMemo, useState, useContext, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
//import { useAuth } from '@clerk/react'
import {
  Separator,
  Spinner,
  YStack,
  useToastController,
  DataLoadingDialog,
  ProgressStepper,
  InlineProgressStepper,
  SimpleText,
  useMedia,
  XStack,
  Group,
  H2,
  H3,
  useWindowDimensions
} from '@my/ui'
import type { ProgressStepperProps } from '@my/ui'
import { color, LongPollingSvc, TZABrandGuidelines, useAuthedRequestFn, useLink, useNavigate, useAuth } from '@my/util'
import assets from '../../assets/images'
import { APP_ENV  } from '@my/config'

import { FinancialProfileForm, FinancialProfileTableOfContents } from './form'
import { FinancialReviewHeading, HomeMainHeading } from '../../components/index'
import { stepLabels } from './data'
import { SidebarStateContext } from '../../provider'
import { Platform } from 'react-native'

export function FinancialProfileScreen() {
  const { width: windowWidth } = useWindowDimensions();
  const isSmallerWindow = windowWidth <= 600;
  const { userId } = useAuth()
  const sidebarState = useContext(SidebarStateContext)
  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()
  const { sm } = useMedia()
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0)
  const [dataOpTs, setDataOpTs] = useState(0);
  const [formSubmitting, setFormSubmittingCore] = useState('false');
  const setFormSubmitting = (val: boolean | string) => {
    setFormSubmittingCore(String(val));
    setDataOpTs(Date.now());
  }

  const timeSinceDataOp = APP_ENV.TZA_ENV !== 'production'
  ? <SimpleText>{(Date.now() - dataOpTs) / 1000} seconds</SimpleText>
  : null

  const [buttonFns, setButtonFns] = useState<ProgressStepperProps['buttonFns']>({
    discard: {
      disabled: true,
      fn: () => Promise.resolve()
    },
    save: {
      disabled: true,
      fn: () => Promise.resolve()
    },
    submit: {
      disabled: true,
      fn: () => Promise.resolve()
    }
  });

  const progressValue = useMemo(
    () => Math.round((currentStep / stepLabels.length) * 100),
    [currentStep, stepLabels]
  )

  const formDataQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  })

  const mutation = useMutation({
    mutationFn: (data: any) =>
      authedRequestFn('/financial-profile', { method: 'POST', body: data }),
    onMutate: () => {
      setFormSubmitting(true);
      LongPollingSvc.trigger('financial-review');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-profile'] })
      toast.show('Successfully saved!', {
        message: 'Your updated Financial Review will be available shortly.',
      })
    },
    onSettled: () => {
      setFormSubmitting(false);
      //@ts-ignore
      window.scrollTo(0,0);
      navigate('/');
    },
  })

  const saveMutation = useMutation({
    mutationFn: (data: any) =>
      authedRequestFn('/save-financial-profile', { method: 'POST', body: data }),
    onMutate: () => setFormSubmitting('save'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-profile'] })
      toast.show('Successfully saved!', { message: 'Your data has been saved.' })
    },
    onSettled: () => setFormSubmitting(false),
    onError: (error) => {
      //console.log(error)
    },
  })

  const discardMutation = useMutation({
    mutationFn: (data: any) =>
      authedRequestFn('/discard-financial-profile', { method: 'DELETE', body: data }),
    onMutate: () => setFormSubmitting('discard'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-profile'] })
      toast.show('Successfully discarded!', { message: 'Your data removed successfully.' })
    },
    onSettled: () => setFormSubmitting(false),
  })
  const refreshFinancialInstitution = async (institution_obj: any) => {
    const { institution_id } = institution_obj.institution
    const response = await authedRequestFn('/plaid/account_data', {
      query: { userId, institution_id },
    })
    return response
  }

  const handleStepChange = (stepNumber: number) => {
    if (Platform.OS === 'web') {
      window.scrollTo(0,0);
    }
    setCurrentStep(stepNumber)
  }

  const onSubmit = (data: any, formState: { errors: any, isDirty: boolean, dirtyFields: any }) => {
    const submitFn = () => mutation.mutate(data);
    if (formState.isDirty) {
      saveMutation.mutate(data, { onSettled: submitFn });
    } else {
      submitFn();
    }
  };
  const onSave = (data: any, formState: { errors: any, isDirty: boolean, dirtyFields: any }) => saveMutation.mutate(data)
  const onDiscard = (data: any) => discardMutation.mutate(data)

  const form = currentStep === 0 ? <FinancialProfileTableOfContents 
      handleStepChange={handleStepChange}
      stepLabels={stepLabels}
    /> : !formDataQuery.isLoading && formDataQuery.data ? (
          <FinancialProfileForm
            data={formDataQuery.data}
            onSubmit={onSubmit}
            refreshFinancialInstitution={refreshFinancialInstitution}
            onDiscard={onDiscard}
            onSave={onSave}
            currentStep={currentStep}
            handleStepChange={handleStepChange}
            stepLabels={stepLabels}
            reportButtonFns={setButtonFns}
          />
        ) : (
          <Spinner size="large" color="$green10" />
        )

  const inlineStepper = isSmallerWindow ? <InlineProgressStepper
    currentStep={currentStep}
    handleStepChange={handleStepChange}
    stepLabels={stepLabels}
    progressValue={progressValue}
    sidebarState={sidebarState}
    buttonFns={buttonFns}
  /> : <></>;
  return (
    <YStack height={'100%'} bc="$background" p="$4" gap={20} /* $platform-web={{ padding: sm ? 0 : '$4' }} */ bg="#fff">
      <YStack width={'100%'} gap={isSmallerWindow ? 5 : 20} style={{
        backgroundColor: 'white', /* flexGrow: 1, */ marginTop: -18, paddingTop: 18, 
        position: 'sticky', top: Platform.select({ web: isSmallerWindow ? 48 : 78, native: 0 }), zIndex: 2
      }}>
        <FinancialReviewHeading
          title="Financial Profile"
          img={assets.profile}
          imageStyle={{
            //'$platform-native': { width: 26, height: 22 },
            //'$platform-web': { width: 24, height: 26 },
            resizeMode: 'contain',
          }}
          dynamicSubtitle={currentStep === 0 ? <></> : inlineStepper}
        />
        {/* <HomeMainHeading type="Financial Profile" image={assets.profile} displayNative /> */}
        {/* <SimpleText fontSize={20} color={color.tzaGreen} fontWeight={'700'}>
          Let's build your financial profile
        </SimpleText> */}
          
        {currentStep === 0 ? null : <ProgressStepper
          currentStep={currentStep}
          handleStepChange={handleStepChange}
          stepLabels={stepLabels}
          progressValue={progressValue}
          sidebarState={sidebarState}
          buttonFns={buttonFns}
          minimal={isSmallerWindow}
        />}
      </YStack>
        {form}
      {/* <Separator /> */}
      <DataLoadingDialog open={formSubmitting !== 'false'} onOpenChange={setFormSubmitting}>
        <YStack alignItems='center' gap={15}>
          {formSubmitting === 'true'
            ? <>
              <H2 color={TZABrandGuidelines.colorsMap.primaryGreen}>Your Financial Profile has been submitted!</H2>
              <H3>Parity is now analyzing and thinking about your data...</H3>
              <SimpleText fontSize={18}>Go grab a cup of coffee ☕ and come back in 1-2 minutes! </SimpleText>
              <SimpleText>{Platform.select({web: `(Don't close your browser!)`, native:  `(Don't close the app!)`})}</SimpleText>
            </> : null
          }
          <Spinner size="large" color="$green10" />
          {false && timeSinceDataOp}
        </YStack>
      </DataLoadingDialog>
    </YStack>
  )
}
